/**
 * indianStatesHeritage — reference data for state landing pages under
 * /state/:stateSlug. Each entry is a compact, editorial-safe summary
 * covering the four buckets we surface on the page:
 *
 *   • history          — 1–2 sentence overview
 *   • languages        — native tongues
 *   • famousFamilies   — {name, note}  (well-known lineages / dynasties)
 *   • communities      — major named community groups / castes / clans
 *   • gotras           — common gotras with roots in this state
 *   • corridors        — {to, note} (well-known outward migration corridors)
 *
 * Keep entries factual, dignified, and tourism-safe. Never surface
 * caste hierarchies or contested claims — this is a heritage-archive
 * page, not a debate forum.
 */

export const STATE_HERITAGE = {
  "kerala": {
    name: "Kerala",
    capital: "Thiruvananthapuram",
    region: "South",
    formed: 1956,
    languages: ["Malayalam"],
    history: "The Malabar Coast has been a spice-trade meeting point for over 2,000 years — welcoming Roman, Arab, Chinese and European merchants. Modern Kerala was formed in 1956 by uniting Malayalam-speaking regions of the Madras Presidency and the princely states of Travancore and Cochin.",
    famousFamilies: [
      { name: "Zamorins of Calicut",           note: "Ruling family who welcomed Vasco da Gama in 1498." },
      { name: "Travancore Royal Family",        note: "Warriors, patrons of Sanskrit and Carnatic arts." },
      { name: "Kochi Royal Family",             note: "Custodians of the Mattancherry palace and Paradesi synagogue." },
      { name: "Namboothiri Illams",             note: "Ancient Brahmin households of ritual and land." },
      { name: "Kurup / Nair Tharavadus",        note: "Matrilineal joint-family houses across central Kerala." },
    ],
    communities: ["Nair", "Ezhava/Thiyya", "Namboothiri", "Syrian Christian", "Latin Catholic", "Mappila Muslim", "Cochin Jew", "Pulaya"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Vishwamitra", "Angirasa", "Atri"],
    corridors: [
      { to: "Bombay / Mumbai",        note: "1930s–70s — nursing, clerical, and shipping jobs." },
      { to: "Persian Gulf (UAE, KSA)", note: "1970s onward — construction, healthcare, retail." },
      { to: "United States",           note: "1965 Immigration Act — doctors, engineers, nurses." },
      { to: "Melbourne / Sydney",      note: "2000s — IT, healthcare, hospitality." },
    ],
  },

  "tamil-nadu": {
    name: "Tamil Nadu",
    capital: "Chennai",
    region: "South",
    formed: 1969,
    languages: ["Tamil"],
    history: "Home to one of the world's oldest continuously spoken languages, Tamil Nadu carries the legacy of the Chola, Chera, Pandya, and Pallava dynasties. Formed as Madras State in 1950, it was renamed Tamil Nadu in 1969.",
    famousFamilies: [
      { name: "Chola Dynasty",              note: "Built Brihadeeswara temple; naval empire reached Sumatra." },
      { name: "Pandya Dynasty",             note: "Rulers of Madurai — patrons of Tamil literature." },
      { name: "Nattukottai Chettiars",       note: "Merchant-banker community of Chettinad — trade across Southeast Asia." },
      { name: "Iyer / Iyengar Agraharam",   note: "Brahmin scholarly households of Thanjavur and Tirunelveli." },
      { name: "Vellalar landowning families", note: "Traditional agrarian dynasties of the Kaveri delta." },
    ],
    communities: ["Iyer", "Iyengar", "Vellalar", "Chettiar", "Mudaliar", "Reddiar", "Nadar", "Isai Vellalar"],
    gotras: ["Bharadwaja", "Kaushika", "Kashyapa", "Shrivatsa", "Harita", "Vadhula"],
    corridors: [
      { to: "Sri Lanka",           note: "Colonial-era plantation labour; later Tamil refugees." },
      { to: "Malaysia / Singapore", note: "1800s–1930s — plantation and railway labour." },
      { to: "United States",       note: "IT and medical migration since the 1970s." },
      { to: "United Kingdom",      note: "NHS and academia — Ceylon Tamil families via London." },
    ],
  },

  "karnataka": {
    name: "Karnataka",
    capital: "Bengaluru",
    region: "South",
    formed: 1956,
    languages: ["Kannada"],
    history: "Karnataka was the seat of the Chalukya, Hoysala, and Vijayanagara empires. The Kannada-speaking regions were united in 1956 as Mysore State, renamed Karnataka in 1973.",
    famousFamilies: [
      { name: "Wodeyars of Mysore",        note: "24-generation royal family; patrons of Mysore Dasara." },
      { name: "Vijayanagara Rayas",         note: "Emperors who ruled Hampi in the 14th–16th centuries." },
      { name: "Havyaka Brahmins",           note: "Coastal Uttara Kannada agrarian priestly households." },
      { name: "Nadaprabhus of Bangalore",  note: "The Kempegowda family — founders of Bengaluru." },
      { name: "Coorgi (Kodava) Ain-Mane",  note: "Ancestral homes of the martial Kodava clans." },
    ],
    communities: ["Vokkaliga", "Lingayat", "Kodava", "Havyaka", "Bunts", "Konkani", "Beary", "Kuruba"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Angirasa", "Gautama", "Atri"],
    corridors: [
      { to: "Bombay / Pune",          note: "Textile and film industry; Konkani diaspora." },
      { to: "United States",           note: "Bengaluru's IT boom → Silicon Valley since the 1990s." },
      { to: "London / Manchester",    note: "Doctors and engineers, 1960s–80s." },
      { to: "Middle East",             note: "Coastal Karnataka — Beary and Konkani professionals." },
    ],
  },

  "andhra-pradesh": {
    name: "Andhra Pradesh",
    capital: "Amaravati",
    region: "South",
    formed: 1956,
    languages: ["Telugu"],
    history: "The Telugu-speaking lands of the Satavahana, Kakatiya and Vijayanagara empires. Modern Andhra Pradesh was formed in 1956; Telangana separated in 2014.",
    famousFamilies: [
      { name: "Kakatiya Dynasty",         note: "Warangal rulers, builders of the Thousand Pillar Temple." },
      { name: "Reddy zamindar families",  note: "Landowning lineages of coastal AP and Rayalaseema." },
      { name: "Kamma agrarian families",  note: "Farmers of Krishna and Guntur — later industry titans." },
      { name: "Niyogi Brahmin lineages",  note: "Administrators of the Nizam and colonial courts." },
      { name: "Deshmukh houses",           note: "Revenue-collectors of the Deccan plateau." },
    ],
    communities: ["Reddy", "Kamma", "Kapu", "Niyogi", "Vaidiki", "Velama", "Balija", "Yadava"],
    gotras: ["Vasishta", "Bharadwaja", "Kashyapa", "Gautama", "Harita", "Kaushika"],
    corridors: [
      { to: "New Jersey / Texas / California", note: "IT and healthcare — largest US Telugu diaspora." },
      { to: "Chennai / Bangalore",              note: "Film and technology industries." },
      { to: "Persian Gulf",                     note: "Nursing, IT, engineering across Kuwait and UAE." },
      { to: "Fiji / Mauritius",                  note: "19th-century indentured labour on sugar plantations." },
    ],
  },

  "telangana": {
    name: "Telangana",
    capital: "Hyderabad",
    region: "South",
    formed: 2014,
    languages: ["Telugu", "Urdu"],
    history: "India's youngest state, formed in 2014 from the northwestern districts of Andhra Pradesh. Home to the 400-year-old city of Hyderabad — capital of the Qutb Shahi and Nizam dynasties.",
    famousFamilies: [
      { name: "Nizams of Hyderabad",       note: "Asaf Jahi dynasty ruling from 1724 to 1948." },
      { name: "Salar Jung family",         note: "Nizam prime ministers; Salar Jung museum founders." },
      { name: "Deshmukh houses",            note: "Revenue-collector lineages of Telangana villages." },
      { name: "Velama zamindars",           note: "Warrior-landholder families of northern Telangana." },
    ],
    communities: ["Reddy", "Velama", "Deshmukh", "Mudiraj", "Padmashali", "Hyderabadi Muslim", "Marwari"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Gautama", "Harita"],
    corridors: [
      { to: "United States",               note: "Hyderabad → IT capitals of the world." },
      { to: "Persian Gulf",                 note: "Hyderabadi labour and business across the UAE and KSA." },
      { to: "Bombay / Mumbai",             note: "Cinema and hospitality — the Hyderabadi biryani belt." },
    ],
  },

  "maharashtra": {
    name: "Maharashtra",
    capital: "Mumbai",
    region: "West",
    formed: 1960,
    languages: ["Marathi"],
    history: "Home of the Maratha Confederacy under Chhatrapati Shivaji, and the commercial heart of modern India. Formed in 1960 by carving Marathi districts out of the erstwhile Bombay State.",
    famousFamilies: [
      { name: "Bhonsle Chhatrapatis",       note: "Descendants of Shivaji Maharaj — Satara and Kolhapur lines." },
      { name: "Peshwa Bhat family",         note: "Chitpavan Brahmin prime ministers of the Marathas." },
      { name: "Deshmukh Patil families",    note: "Village revenue lineages across the Deccan." },
      { name: "Chitpavan Konkanastha",      note: "Coastal Konkan Brahmin scholarly households." },
      { name: "Bombay Parsi families",      note: "Tata, Wadia, Godrej — industrial pioneers of modern India." },
    ],
    communities: ["Maratha", "Kunbi", "Chitpavan", "Deshastha", "Karhade", "Saraswat", "Agri-Koli", "Parsi", "East Indian"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Gautama", "Atri", "Vishwamitra"],
    corridors: [
      { to: "Village → Mumbai",             note: "The quintessential Marathi-Hindi migration story." },
      { to: "United States",                note: "Marathi IT and academic diaspora since the 1970s." },
      { to: "Persian Gulf",                  note: "Konkan Muslim and Christian professionals." },
    ],
  },

  "gujarat": {
    name: "Gujarat",
    capital: "Gandhinagar",
    region: "West",
    formed: 1960,
    languages: ["Gujarati"],
    history: "A coastal trading powerhouse for millennia — home to the Solanki, Chaulukya and Sultanate dynasties. Modern Gujarat was carved out of Bombay State in 1960 alongside Maharashtra.",
    famousFamilies: [
      { name: "Gaekwads of Baroda",        note: "Progressive Maratha rulers of Vadodara." },
      { name: "Ambanis of Chorwad",         note: "Reliance industrial family from Junagadh." },
      { name: "Nagar Brahmin lineages",    note: "Scholar-administrators of Junagadh and Vadnagar." },
      { name: "Patel / Patidar families",  note: "Land-holding farmers of Charotar — now global entrepreneurs." },
      { name: "Kutchi Bhatia merchant houses", note: "Shippers who linked Mandvi to East Africa and Zanzibar." },
    ],
    communities: ["Patel", "Patidar", "Brahmin", "Bania", "Jain", "Kutchi", "Kadwa", "Leva", "Parsi", "Rajput"],
    gotras: ["Kashyapa", "Bharadwaja", "Vasishta", "Gautama", "Angirasa", "Vachhas"],
    corridors: [
      { to: "East Africa (Uganda, Kenya)", note: "1890s onward — dukawalas, contractors, industrialists." },
      { to: "United Kingdom",               note: "Post-1972 Uganda expulsion — the Leicester diaspora." },
      { to: "United States",                note: "Motels and diamonds — the Patel and Palanpuri families." },
      { to: "Canada / Australia",           note: "Post-2000 professional migration." },
    ],
  },

  "rajasthan": {
    name: "Rajasthan",
    capital: "Jaipur",
    region: "North",
    formed: 1956,
    languages: ["Rajasthani", "Hindi"],
    history: "The land of kings — home to the Rajput clans of Mewar, Marwar, Amber, Bikaner and Jaisalmer. Consolidated as Rajasthan between 1949 and 1956 from 22 princely states.",
    famousFamilies: [
      { name: "Sisodias of Mewar",         note: "Longest continuous Rajput royal line — Udaipur." },
      { name: "Rathores of Marwar",         note: "Jodhpur ruling house; Umaid Bhawan builders." },
      { name: "Kachwahas of Amber",         note: "Ancestors of the Jaipur royal family." },
      { name: "Bhatis of Jaisalmer",        note: "Guardians of the Silk Route desert kingdom." },
      { name: "Marwari trading houses",     note: "Birla, Bajaj, Poddar — industrial magnates of India." },
    ],
    communities: ["Rajput", "Brahmin", "Marwari", "Jat", "Gurjar", "Meena", "Bishnoi", "Jain"],
    gotras: ["Chauhan", "Rathore", "Sisodia", "Kachwaha", "Solanki", "Parmar", "Bhati", "Tomar"],
    corridors: [
      { to: "Kolkata / Bengal",             note: "Marwari traders following the Ganga since the 1800s." },
      { to: "Mumbai",                        note: "Bollywood financiers and jewellery merchants." },
      { to: "United States",                 note: "Motel and hospitality Rajput / Marwari families." },
      { to: "Hong Kong / Singapore",         note: "Marwari trading houses' Asian expansion." },
    ],
  },

  "punjab": {
    name: "Punjab",
    capital: "Chandigarh",
    region: "North",
    formed: 1966,
    languages: ["Punjabi"],
    history: "The homeland of the Sikh faith, the Punjabi language and the Green Revolution. The modern state was carved out in 1966 after Haryana and Himachal were separated.",
    famousFamilies: [
      { name: "Patiala Royal Family",      note: "Sikh Phulkian dynasty — princes of the Raj era." },
      { name: "Kapurthala Royal Family",    note: "Ahluwalia rulers famed for the French-style palace." },
      { name: "Ranas of Nabha",             note: "Sikh Phulkian rulers of a princely state near Patiala." },
      { name: "Bedis of Dera Baba Nanak",  note: "Descendants of Guru Nanak Dev Ji." },
    ],
    communities: ["Jat Sikh", "Khatri", "Arora", "Ramgarhia", "Ravidasia", "Saini", "Kamboj", "Punjabi Muslim (historically)"],
    gotras: ["Cheema", "Sidhu", "Gill", "Sandhu", "Bajwa", "Dhillon", "Grewal", "Sekhon", "Randhawa"],
    corridors: [
      { to: "United Kingdom (Southall)",    note: "1950s–70s — foundry and textile jobs." },
      { to: "Canada (Brampton, Surrey)",     note: "Continuous Punjabi migration since 1897." },
      { to: "United States (California)",   note: "Yuba City farming Sikhs since the early 1900s." },
      { to: "Australia (Melbourne)",         note: "Students and skilled workers post-2000." },
      { to: "Italy / Germany",               note: "Recent European labour migration." },
    ],
  },

  "delhi": {
    name: "Delhi",
    capital: "New Delhi",
    region: "North",
    formed: 1956,
    languages: ["Hindi", "Punjabi", "Urdu"],
    history: "Capital of empires from the Tomars and Chauhans through the Delhi Sultanate, the Mughals, the British Raj and independent India. Delhi has been rebuilt at least seven times.",
    famousFamilies: [
      { name: "Timurid / Mughal dynasty",  note: "Rulers from Babur (1526) to Bahadur Shah Zafar (1857)." },
      { name: "Delhi Punjabi refugee families", note: "Post-1947 arrivals from Lahore, Multan, Rawalpindi." },
      { name: "Delhi Kayastha families",   note: "Administrative households of pre-Partition Delhi." },
    ],
    communities: ["Punjabi (post-1947)", "Kayastha", "Bania", "Jat", "Gujjar", "Muslim (Old Delhi)", "Bengali"],
    gotras: ["Kashyapa", "Bharadwaja", "Vasishta", "Gautama"],
    corridors: [
      { to: "United States",               note: "Techies and doctors from Delhi's colleges." },
      { to: "London / Toronto",             note: "Second-generation Punjabi families across the Commonwealth." },
    ],
  },

  "uttar-pradesh": {
    name: "Uttar Pradesh",
    capital: "Lucknow",
    region: "North",
    formed: 1950,
    languages: ["Hindi", "Urdu", "Awadhi", "Bhojpuri"],
    history: "The demographic heart of India — home to Ayodhya, Varanasi, Prayagraj and Mathura. Historically the seat of the Nawabs of Awadh and, before them, the Mughal court.",
    famousFamilies: [
      { name: "Nawabs of Awadh",           note: "Shia rulers who built Lucknow's Bara Imambara." },
      { name: "Nehru–Gandhi family",       note: "The Kashmiri Pandit lineage of Allahabad." },
      { name: "Kayastha and Bhumihar houses", note: "Administrators of the Ganga plain." },
      { name: "Awadhi zamindar families",  note: "Land-revenue lineages across Awadh." },
    ],
    communities: ["Brahmin", "Kshatriya (Thakur)", "Kayastha", "Bhumihar", "Bania", "Yadav", "Jat", "Muslim (Awadhi, Rohilla)"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Gautama", "Shandilya", "Kaushika"],
    corridors: [
      { to: "Fiji / Mauritius / Guyana / Suriname", note: "1834–1917 indentured labour ('Girmitiya')." },
      { to: "Persian Gulf",                          note: "Awadhi and Bhojpuri labour migration since the 1970s." },
      { to: "Bombay",                                 note: "Bhaiya migration to Mumbai's mills, taxis, dabbawalas." },
    ],
  },

  "west-bengal": {
    name: "West Bengal",
    capital: "Kolkata",
    region: "East",
    formed: 1947,
    languages: ["Bengali"],
    history: "Cradle of the Bengal Renaissance and India's freedom movement. Partitioned in 1947 — East Bengal became East Pakistan, then Bangladesh in 1971.",
    famousFamilies: [
      { name: "Tagore family of Jorasanko", note: "Rabindranath, Abanindranath — Bengal Renaissance." },
      { name: "Roy Choudhurys",             note: "Zamindar houses across Bengal — Rathindra, Prafulla." },
      { name: "Bhattacharya, Chatterjee, Banerjee, Mukherjee", note: "Kulin Brahmin lineages of Bengal." },
      { name: "Marwari houses of Kolkata",  note: "Birla and Poddar — arrived in the late 1800s." },
    ],
    communities: ["Brahmin (Kulin)", "Kayastha", "Baidya", "Namasudra", "Marwari", "Bengali Muslim", "Anglo-Indian"],
    gotras: ["Bharadwaja", "Kashyapa", "Sandilya", "Vatsa", "Gautama"],
    corridors: [
      { to: "London / Oxford",              note: "First-wave scholars and ICS officers from Presidency College." },
      { to: "USA (New Jersey, California)", note: "Bengali academic and medical diaspora since the 1960s." },
      { to: "Dubai / Middle East",           note: "Recent professional migration." },
    ],
  },

  "odisha": {
    name: "Odisha",
    capital: "Bhubaneswar",
    region: "East",
    formed: 1936,
    languages: ["Odia"],
    history: "Ancient Kalinga — famed for the emperor Ashoka's conversion after the Kalinga war and for the temples of Konark and Puri. First linguistic state in India, formed in 1936.",
    famousFamilies: [
      { name: "Gajapati kings of Puri",    note: "Custodians of the Jagannath Temple to this day." },
      { name: "Bhanja rulers of Mayurbhanj", note: "Patrons of arts and Chhau dance." },
      { name: "Karana Kayasthas",           note: "Administrative lineages of the Odia courts." },
    ],
    communities: ["Brahmin (Utkala)", "Karan (Kayastha)", "Khandayat", "Gopal", "Odia Muslim", "Adivasi (Kondh, Santhal)"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Gautama", "Krushnathreya"],
    corridors: [
      { to: "Gujarat / Surat",             note: "Odia labour in the diamond and textile industries." },
      { to: "Kolkata / Mumbai",             note: "Domestic and skilled labour migration." },
    ],
  },

  "bihar": {
    name: "Bihar",
    capital: "Patna",
    region: "East",
    formed: 1936,
    languages: ["Hindi", "Bhojpuri", "Maithili", "Magadhi"],
    history: "Ancient Magadha — birthplace of the Mauryan and Gupta empires, and of Buddhism and Jainism. Nalanda University drew scholars for a millennium.",
    famousFamilies: [
      { name: "Darbhanga Raj family",      note: "Zamindars of Mithila — Maithili patrons." },
      { name: "Hathwa Raj family",          note: "One of Bihar's largest zamindaris." },
      { name: "Bhumihar and Rajput houses",  note: "Land-revenue lineages of Magadh and Bhojpur." },
    ],
    communities: ["Brahmin (Maithil)", "Bhumihar", "Rajput", "Kayastha", "Yadav", "Kurmi", "Musahar", "Bihari Muslim"],
    gotras: ["Kashyapa", "Bharadwaja", "Vasishta", "Gautama", "Sandilya", "Vatsa"],
    corridors: [
      { to: "Mauritius / Fiji / Suriname", note: "The largest source of 19th-century indenture." },
      { to: "Punjab / Delhi (post-1990)",    note: "Farm and construction labour migration." },
      { to: "Persian Gulf",                  note: "Skilled and unskilled labour since the 1980s." },
    ],
  },

  "assam": {
    name: "Assam",
    capital: "Dispur",
    region: "Northeast",
    formed: 1947,
    languages: ["Assamese", "Bodo"],
    history: "The Ahom kingdom ruled the Brahmaputra valley for 600 years, resisting Mughal invasions. The tea gardens, first planted by the British in the 1830s, defined its modern economy.",
    famousFamilies: [
      { name: "Ahom royal family",          note: "Tai-origin dynasty (1228–1826) — nowadays merged into Assamese identity." },
      { name: "Koch royal family",          note: "Northwestern Assam's medieval rulers." },
      { name: "Zamindar houses",             note: "Land-revenue lineages of the Brahmaputra valley." },
    ],
    communities: ["Ahom", "Assamese Brahmin", "Kalita", "Bodo", "Karbi", "Assamese Muslim", "Bengali (Bhatia)", "Adivasi (tea garden)"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Vashista", "Sandilya"],
    corridors: [
      { to: "Delhi / Bangalore",            note: "Education and IT migration since the 1990s." },
      { to: "United Kingdom",                note: "Doctors and academics from Guwahati." },
    ],
  },

  "meghalaya": {
    name: "Meghalaya",
    capital: "Shillong",
    region: "Northeast",
    formed: 1972,
    languages: ["Khasi", "Garo", "English"],
    history: "The 'abode of clouds' — home to matrilineal Khasi and Garo tribes, one of the world's few matrilineal societies where lineage and property pass through daughters.",
    famousFamilies: [
      { name: "Khasi Syiem clans",          note: "Traditional chief families of the Khasi Hills." },
      { name: "Garo Nokma families",         note: "Village headperson lineages of the Garo Hills." },
    ],
    communities: ["Khasi", "Jaintia", "Garo", "Assamese", "Bengali", "Nepali"],
    corridors: [
      { to: "United States / UK",           note: "Education and church-linked migration." },
    ],
  },

  "haryana": {
    name: "Haryana",
    capital: "Chandigarh",
    region: "North",
    formed: 1966,
    languages: ["Hindi", "Haryanvi"],
    history: "The Kurukshetra plains of the Mahabharata. Carved out of Punjab in 1966 on linguistic lines; today India's leading agricultural, sports and manufacturing state.",
    famousFamilies: [
      { name: "Jat khap panchayats",       note: "Traditional clan councils governing rural society." },
      { name: "Ahulwalia and Bishnoi lineages", note: "Ecology-focused farming communities." },
    ],
    communities: ["Jat", "Yadav", "Gurjar", "Brahmin", "Rajput", "Bishnoi", "Ahir", "Ror"],
    gotras: ["Sheoran", "Malik", "Dahiya", "Beniwal", "Chahar", "Poonia", "Kadyan", "Sangwan"],
    corridors: [
      { to: "United States / Canada",       note: "Jat farming families to the diaspora." },
      { to: "United Kingdom",                note: "Post-1960s labour migration." },
    ],
  },

  "himachal-pradesh": {
    name: "Himachal Pradesh",
    capital: "Shimla",
    region: "North",
    formed: 1971,
    languages: ["Hindi", "Pahari"],
    history: "The 'land of snow' — historically ruled by small hill kingdoms like Kangra, Chamba, Mandi, Kullu and Bilaspur. Attained full statehood in 1971.",
    famousFamilies: [
      { name: "Kangra Katoch dynasty",     note: "One of the oldest surviving royal lineages in the world." },
      { name: "Chamba Rajas",                note: "Rulers of a Himalayan kingdom for over a thousand years." },
    ],
    communities: ["Rajput", "Brahmin", "Gaddi", "Kinnauri", "Lahauli", "Kanet"],
    gotras: ["Kashyapa", "Bharadwaja", "Vasishta", "Katoch"],
    corridors: [
      { to: "Delhi / Chandigarh",           note: "Education and government service." },
      { to: "United Kingdom",                note: "Kangra-Punjab migration to the West Midlands." },
    ],
  },

  "goa": {
    name: "Goa",
    capital: "Panaji",
    region: "West",
    formed: 1987,
    languages: ["Konkani"],
    history: "Portuguese colony for 451 years (1510–1961) — India's smallest state with a unique Konkani-Portuguese-Marathi cultural blend.",
    famousFamilies: [
      { name: "Bardez and Salcete Bhandari houses", note: "Traditional Konkani families." },
      { name: "Catholic Goan families",     note: "Descaralho, Fernandes, D'Souza — Portuguese-era lineages." },
      { name: "Saraswat Brahmin houses",   note: "Trading and administrative families across the Konkan." },
    ],
    communities: ["Goud Saraswat Brahmin", "Kudumbi", "Kunbi", "Catholic (Goan)", "Konkani Muslim", "Kharvi"],
    gotras: ["Kashyapa", "Bharadwaja", "Vasishta", "Kaundinya", "Atri"],
    corridors: [
      { to: "Bombay / Pune",                note: "Goan Catholic musicians, chefs, hospitality workers." },
      { to: "Portugal / UK",                 note: "Post-1961 European migration with Portuguese passports." },
      { to: "Persian Gulf",                  note: "Hospitality and merchant navy workers." },
    ],
  },

  "madhya-pradesh": {
    name: "Madhya Pradesh",
    capital: "Bhopal",
    region: "Central",
    formed: 1956,
    languages: ["Hindi"],
    history: "The heart of India — home to the Bhopal Begums, Bundela Rajputs and Malwa Sultans. The Khajuraho and Sanchi UNESCO sites lie within its borders.",
    famousFamilies: [
      { name: "Scindia family of Gwalior", note: "Maratha rulers turned modern parliamentarians." },
      { name: "Holkars of Indore",           note: "Ahilyabai Holkar's legacy — patrons of temples." },
      { name: "Bhopal Begums",               note: "Four generations of female Muslim rulers." },
      { name: "Bundela Rajputs",              note: "Rulers of Orchha and Datia." },
    ],
    communities: ["Brahmin", "Rajput", "Bania", "Jat", "Kurmi", "Ahir", "Gond", "Bhil"],
    gotras: ["Bharadwaja", "Kashyapa", "Vasishta", "Gautama", "Chauhan", "Parmar"],
    corridors: [
      { to: "Delhi / Bombay",               note: "Business and civil service migration." },
      { to: "Persian Gulf",                  note: "Bhopal labour and professional migration." },
    ],
  },

  "chhattisgarh": {
    name: "Chhattisgarh",
    capital: "Raipur",
    region: "Central",
    formed: 2000,
    languages: ["Chhattisgarhi", "Hindi"],
    history: "Carved out of Madhya Pradesh in 2000. Historically the land of the Haihaya Kalachuris and, before them, the Sabaras — mentioned in the Ramayana.",
    famousFamilies: [
      { name: "Kalachuri Haihayas",         note: "Medieval rulers of Ratanpur." },
      { name: "Bastar royal family",         note: "Rulers of the ancient Bastar kingdom." },
    ],
    communities: ["Brahmin", "Rajput", "Kurmi", "Sahu", "Gond", "Halba", "Kanwar"],
    corridors: [
      { to: "Nagpur / Raipur",              note: "Regional labour migration within central India." },
    ],
  },

  "jharkhand": {
    name: "Jharkhand",
    capital: "Ranchi",
    region: "East",
    formed: 2000,
    languages: ["Hindi", "Santhali", "Ho", "Mundari"],
    history: "The 'land of forests' — home to India's largest tribal population. Carved out of Bihar in 2000 after a century-long movement for a separate state.",
    famousFamilies: [
      { name: "Santhal Manjhi lineages",   note: "Traditional village headman families." },
      { name: "Zamindar houses of Chota Nagpur", note: "Land-revenue lineages under the Raj." },
    ],
    communities: ["Santhal", "Munda", "Ho", "Oraon", "Kurmi", "Bhumihar", "Rajput"],
    corridors: [
      { to: "Assam (tea gardens)",          note: "Colonial-era Adivasi labour migration." },
      { to: "Persian Gulf",                  note: "Modern labour migration from Ranchi and Dhanbad." },
    ],
  },

  "manipur":            { name: "Manipur",           capital: "Imphal",   region: "Northeast", formed: 1972, languages: ["Meitei"],  history: "Home of the Meitei and hill peoples; the classical dance form Manipuri is a UNESCO Intangible Heritage.", famousFamilies: [{ name: "Ningthouja dynasty", note: "Meitei royal family for over 2,000 years." }], communities: ["Meitei", "Naga", "Kuki", "Meitei Pangal (Muslim)"], corridors: [{ to: "Delhi / Bangalore", note: "Education, hospitality, and armed forces." }] },
  "mizoram":            { name: "Mizoram",           capital: "Aizawl",   region: "Northeast", formed: 1987, languages: ["Mizo"],    history: "Formerly the Lushai Hills. Attained statehood in 1987 after a 20-year insurgency ended peacefully.", famousFamilies: [{ name: "Sailo chieftain clans", note: "Traditional Mizo ruling families." }], communities: ["Lusei", "Ralte", "Hmar", "Paite", "Chin-Kuki-Mizo"], corridors: [] },
  "nagaland":           { name: "Nagaland",          capital: "Kohima",   region: "Northeast", formed: 1963, languages: ["Naga languages", "English"], history: "Home to 17 Naga tribes — each with its own language and customs.", famousFamilies: [], communities: ["Ao", "Angami", "Sema", "Lotha", "Konyak", "Zeliang"], corridors: [] },
  "sikkim":             { name: "Sikkim",            capital: "Gangtok",  region: "Northeast", formed: 1975, languages: ["Nepali", "Sikkimese", "Lepcha"], history: "Kingdom of the Namgyal dynasty for 333 years — joined the Indian Union in 1975.", famousFamilies: [{ name: "Namgyal royal family", note: "Chogyal rulers of Sikkim from 1642 to 1975." }], communities: ["Nepali", "Lepcha", "Bhutia"], corridors: [] },
  "tripura":            { name: "Tripura",           capital: "Agartala", region: "Northeast", formed: 1972, languages: ["Bengali", "Kokborok"], history: "The Manikya dynasty ruled Tripura for over 500 years — one of the longest continuous royal lines in India.", famousFamilies: [{ name: "Manikya royal family", note: "Rulers of Tripura from ~1400 to 1949." }], communities: ["Bengali", "Tripuri (Debbarma)", "Reang", "Jamatia"], corridors: [] },
  "arunachal-pradesh":  { name: "Arunachal Pradesh", capital: "Itanagar", region: "Northeast", formed: 1987, languages: ["Nyishi", "Adi", "Apatani", "English"], history: "India's easternmost state — the 'land of the dawn-lit mountains'. Home to 26 major indigenous tribes.", famousFamilies: [], communities: ["Nyishi", "Adi", "Apatani", "Monpa", "Tagin"], corridors: [] },
  "uttarakhand":        { name: "Uttarakhand",       capital: "Dehradun", region: "North", formed: 2000, languages: ["Hindi", "Garhwali", "Kumaoni"], history: "Carved out of Uttar Pradesh in 2000. Home to Char Dham — Yamunotri, Gangotri, Kedarnath and Badrinath.", famousFamilies: [{ name: "Katyuris of Kumaon", note: "Medieval Himalayan kingdom." }, { name: "Panwars of Garhwal", note: "Rulers of Garhwal for centuries." }], communities: ["Garhwali Brahmin", "Kumaoni Brahmin", "Rajput", "Bhotia", "Tharu"], corridors: [{ to: "Delhi / Mumbai", note: "Pahari domestic and hospitality migration." }] },
};

// Convenience — allow display-name lookup by slug (fallback returns null).
export function heritageBySlug(slug) {
  if (!slug) return null;
  return STATE_HERITAGE[slug.toLowerCase()] || null;
}
